
import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Download, Copy, Printer, Edit, Check, Save } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import TextToSpeech from '@/components/TextToSpeech';
import { useLanguage } from '@/contexts/LanguageContext';
import DocumentSharing from '@/components/DocumentSharing';

interface ResultDisplayProps {
  result: string;
}

interface SavedDocument {
  id: string;
  content: string;
  date: string;
  title: string;
}

const ResultDisplay: React.FC<ResultDisplayProps> = ({ result }) => {
  const resultRef = useRef<HTMLDivElement>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editedResult, setEditedResult] = useState<string>(result);
  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [showTitleInput, setShowTitleInput] = useState<boolean>(false);
  const { language, t } = useLanguage();

  // Update editedResult whenever result changes (e.g., new generation)
  React.useEffect(() => {
    setEditedResult(result);
    
    // Extract a title from the first line of the result
    const firstLine = result.split('\n')[0] || '';
    setDocumentTitle(firstLine);
  }, [result]);

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  const saveEdits = () => {
    setEditMode(false);
    toast.success(t('message.saved'));
  };

  const copyToClipboard = () => {
    if (!editedResult) return;
    
    navigator.clipboard.writeText(editedResult)
      .then(() => toast.success(t('message.copied')))
      .catch(() => toast.error(t('message.error')));
  };

  const generatePDF = () => {
    if (!editedResult) return;
    
    try {
      const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
      });
      
      // Set RTL for Urdu
      if (language === 'ur') {
        doc.setR2L(true);
      }
      
      // Try using an appropriate font
      doc.setFont("Arial", "normal");
      doc.setFontSize(14);
      
      // Add content
      const textLines = editedResult.split('\n');
      let yPosition = 20;
      
      textLines.forEach(line => {
        // Right align text for RTL
        if (language === 'ur') {
          const textWidth = doc.getStringUnitWidth(line) * doc.getFontSize() / doc.internal.scaleFactor;
          const textOffset = doc.internal.pageSize.getWidth() - 20 - textWidth;
          doc.text(line, Math.max(textOffset, 10), yPosition);
        } else {
          doc.text(line, 10, yPosition);
        }
        yPosition += 8;
      });
      
      // Save PDF
      const filename = language === 'ur' ? "عدالتی_درخواست.pdf" : "legal_document.pdf";
      doc.save(filename);
      toast.success(t('message.saved'));
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error(t('message.error'));
    }
  };

  const handlePrint = () => {
    const printContent = editedResult;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html dir="${language === 'ur' ? 'rtl' : 'ltr'}">
          <head>
            <title>${language === 'ur' ? 'عدالتی درخواست' : 'Legal Document'}</title>
            <meta charset="UTF-8">
            <style>
              body {
                font-family: ${language === 'ur' ? "'Noto Nastaliq Urdu', serif" : "'Arial', sans-serif"};
                padding: 20px;
                direction: ${language === 'ur' ? 'rtl' : 'ltr'};
                text-align: ${language === 'ur' ? 'right' : 'left'};
                font-size: 16px;
                line-height: 1.8;
              }
            </style>
          </head>
          <body>
            <pre style="font-family: ${language === 'ur' ? "'Noto Nastaliq Urdu', serif" : "'Arial', sans-serif"}; white-space: pre-wrap;">${printContent}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      
      // Print after a small delay to ensure content loads
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    } else {
      toast.error(t('message.error'));
    }
  };

  const prepareToSave = () => {
    setShowTitleInput(true);
  };

  const cancelTitleInput = () => {
    setShowTitleInput(false);
  };

  const saveDocument = () => {
    try {
      // Use the document title or extract from first line if not provided
      const title = documentTitle || (language === 'ur' ? 'عدالتی درخواست' : 'Legal Document');
      
      // Create a document object
      const document: SavedDocument = {
        id: Date.now().toString(),
        content: editedResult,
        date: new Date().toISOString(),
        title: title
      };
      
      // Get existing documents or initialize empty array
      const savedDocs = JSON.parse(localStorage.getItem('adaaltBotDocs') || '[]');
      
      // Add new document and save back to localStorage
      savedDocs.push(document);
      localStorage.setItem('adaaltBotDocs', JSON.stringify(savedDocs));
      
      toast.success(t('message.saved'));
      setShowTitleInput(false);
    } catch (error) {
      console.error("Error saving document:", error);
      toast.error(t('message.error'));
    }
  };

  if (!result) {
    return null;
  }

  return (
    <div className="max-w-2xl mx-auto mt-10 px-4">
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2 flex-wrap">
            <Button 
              onClick={copyToClipboard} 
              variant="outline" 
              size="sm"
              className="flex items-center gap-1"
            >
              <Copy size={16} />
              {t('result.copy')}
            </Button>
            <Button 
              onClick={generatePDF} 
              variant="secondary" 
              size="sm"
              className="flex items-center gap-1"
            >
              <Download size={16} />
              {t('result.download')}
            </Button>
            <Button 
              onClick={handlePrint} 
              variant="outline" 
              size="sm"
              className="flex items-center gap-1"
            >
              <Printer size={16} />
              {t('result.print')}
            </Button>
            <Button 
              onClick={prepareToSave} 
              variant="outline" 
              size="sm"
              className="flex items-center gap-1"
            >
              <Save size={16} />
              {t('result.save')}
            </Button>
            <DocumentSharing documentTitle={documentTitle} documentContent={editedResult} />
            <TextToSpeech text={editedResult} />
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={editMode ? saveEdits : toggleEditMode} 
              variant="ghost" 
              size="sm"
              className="flex items-center gap-1"
            >
              {editMode ? <Check size={16} /> : <Edit size={16} />}
              {editMode ? t('result.saveEdits') : t('result.edit')}
            </Button>
            <h3 className="text-xl font-semibold">
              {t('result.title')}
            </h3>
          </div>
        </div>
        
        {showTitleInput && (
          <div className="mb-6 rounded-md border p-4 bg-muted/50">
            <div className="flex justify-between items-center mb-2">
              <Button 
                onClick={cancelTitleInput}
                variant="ghost" 
                size="sm"
              >
                {language === 'ur' ? 'منسوخ کریں' : 'Cancel'}
              </Button>
              <h4 className="font-medium text-sm">
                {language === 'ur' ? 'درخواست کا عنوان' : 'Document Title'}
              </h4>
            </div>
            <div className="flex gap-2 mt-2">
              <Button 
                onClick={saveDocument}
                variant="default" 
                size="sm"
              >
                {language === 'ur' ? 'محفوظ کریں' : 'Save'}
              </Button>
              <Textarea 
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value)}
                className={language === 'ur' ? "text-right font-urdu" : "text-left"}
                placeholder={language === 'ur' ? "درخواست کا عنوان درج کریں..." : "Enter document title..."}
                dir={language === 'ur' ? 'rtl' : 'ltr'}
                rows={1}
              />
            </div>
          </div>
        )}
        
        {editMode ? (
          <Textarea 
            value={editedResult}
            onChange={(e) => setEditedResult(e.target.value)}
            className={`min-h-[300px] text-lg p-4 ${language === 'ur' ? "text-right font-urdu" : "text-left"}`}
            placeholder={language === 'ur' ? "درخواست میں ترمیم کریں..." : "Edit your document..."}
            dir={language === 'ur' ? 'rtl' : 'ltr'}
          />
        ) : (
          <div 
            ref={resultRef}
            className="bg-muted p-4 rounded-md whitespace-pre-wrap"
            dir={language === 'ur' ? 'rtl' : 'ltr'}
            style={{textAlign: language === 'ur' ? 'right' : 'left'}}
          >
            {editedResult}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ResultDisplay;
